import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './AdminDashboard.css';
import { ReactComponent as UserIcon } from '../../icons/user.svg';
import { ReactComponent as GroupIcon } from '../../icons/groups.svg';
import { ReactComponent as AlertIcon } from '../../icons/alert.svg';
import { ReactComponent as SettingsIcon } from '../../icons/settings.svg';

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    activeOrganizations: 0,
    totalUsers: 0,
    totalLicenses: 0,
    expiringLicenses: 0,
  });
  const [recentOrgs, setRecentOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Super Admin Dashboard - Signature Manager";
    
    // Redirect if not super admin
    if (user && user.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }

    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Platform-wide counts come from the server (accurate regardless of list
      // pagination); the org list is only fetched here for the "recent" table.
      const [overview, orgsData] = await Promise.all([
        api.get(endpoints.admin.analytics.overview),
        api.get(endpoints.admin.organizations.list)
      ]);

      const organizations = orgsData.results || orgsData || [];

      setStats({
        totalOrganizations: overview.organizations.total,
        activeOrganizations: overview.organizations.active,
        totalUsers: overview.users.total,
        totalLicenses: overview.licenses.active + overview.licenses.trial + overview.licenses.expired + overview.licenses.suspended,
        expiringLicenses: overview.licenses.expiring_soon,
      });

      // Set recent organizations (last 5, already ordered by -created_at server-side)
      setRecentOrgs(organizations.slice(0, 5));

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError('Dashboard verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, variant, hint, onClick }) => (
    <div className={`admin-stat-card ${variant === 'dark' ? 'admin-stat-card-dark' : ''}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <h3>{value}</h3>
      <p>{title}</p>
      {hint && <p className="admin-stat-card-hint">{hint}</p>}
    </div>
  );

  const QuickAction = ({ icon: Icon, title, description, onClick }) => (
    <div className="quick-action" onClick={onClick}>
      <div className="action-icon">
        <Icon />
      </div>
      <div className="action-content">
        <h4>{title}</h4>
        <p>{description}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading-spinner">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div>
          <p className="dashboard-eyebrow">Platform Yönetimi</p>
          <h1>Süper Admin Dashboard</h1>
          <p className="dashboard-subtitle">Sistem genelinde yönetim ve istatistikler</p>
        </div>
        <button className="refresh-btn" onClick={fetchDashboardData}>
          ↻ Yenile
        </button>
      </div>

      {error && (
        <div className="admin-error-banner">
          <AlertIcon />
          <span>{error}</span>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="admin-stats-grid">
        <StatCard
          title="Toplam Organizasyon"
          value={stats.totalOrganizations}
          onClick={() => navigate('/admin/organizations')}
        />
        <StatCard
          title="Aktif Organizasyon"
          value={stats.activeOrganizations}
          onClick={() => navigate('/admin/organizations')}
        />
        <StatCard
          title="Toplam Kullanıcı"
          value={stats.totalUsers}
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          title="Toplam Lisans"
          value={stats.totalLicenses}
          variant="dark"
          hint={stats.expiringLicenses > 0 ? `${stats.expiringLicenses} lisans yakında doluyor` : null}
          onClick={() => navigate('/admin/licenses')}
        />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h2>Hızlı İşlemler</h2>
        <div className="quick-actions-grid">
          <QuickAction
            icon={GroupIcon}
            title="Yeni Organizasyon"
            description="Yeni bir organizasyon oluştur"
            onClick={() => navigate('/admin/organizations')}
          />
          <QuickAction
            icon={UserIcon}
            title="Kullanıcı Yönetimi"
            description="Tüm kullanıcıları görüntüle ve düzenle"
            onClick={() => navigate('/admin/users')}
          />
          <QuickAction
            icon={SettingsIcon}
            title="Lisans Yönetimi"
            description="Lisansları görüntüle ve düzenle"
            onClick={() => navigate('/admin/licenses')}
          />
          <QuickAction
            icon={AlertIcon}
            title="Sistem Uyarıları"
            description="Kullanıcılara uyarı gönder"
            onClick={() => navigate('/admin/notifications')}
          />
        </div>
      </div>

      {/* Recent Organizations */}
      <div className="recent-section">
        <div className="section-header">
          <h2>Son Eklenen Organizasyonlar</h2>
          <button className="link-btn" onClick={() => navigate('/admin/organizations')}>
            Tümünü Gör →
          </button>
        </div>
        
        {recentOrgs.length > 0 ? (
          <div className="recent-orgs-table">
            <table>
              <thead>
                <tr>
                  <th>Organizasyon</th>
                  <th>Alt Domain</th>
                  <th>Kullanıcı Sayısı</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {recentOrgs.map(org => (
                  <tr key={org.id}>
                    <td>
                      <div className="admin-org-name">
                        <strong>{org.name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="subdomain">{org.subdomain}</span>
                    </td>
                    <td>
                      <span className="user-count">
                        {org.current_usage?.users || 0} / {org.max_users || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-status-badge ${org.is_active ? 'active' : 'inactive'}`}>
                        {org.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="admin-recent-detail-link"
                        onClick={() => navigate('/admin/organizations')}
                      >
                        Detay
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-empty-state">
            <GroupIcon />
            <p>Henüz organizasyon bulunmuyor</p>
            <button className="admin-empty-state-cta" onClick={() => navigate('/admin/organizations')}>
              İlk Organizasyonu Oluştur
            </button>
          </div>
        )}
      </div>

      {/* System Health */}
      <div className="system-health">
        <h2>Sistem Durumu</h2>
        <div className="health-cards">
          <div className="health-card healthy">
            <div className="health-indicator"></div>
            <div>
              <strong>API Sunucusu</strong>
              <p>Çalışıyor</p>
            </div>
          </div>
          <div className="health-card healthy">
            <div className="health-indicator"></div>
            <div>
              <strong>Veritabanı</strong>
              <p>Bağlantı Başarılı</p>
            </div>
          </div>
          <div className={`health-card ${stats.activeOrganizations > 0 ? 'info' : 'warning'}`}>
            <div className="health-indicator"></div>
            <div>
              <strong>Microsoft Graph API</strong>
              <p>{stats.activeOrganizations > 0 ? 'Yapılandırılmış' : 'Yapılandırma Gerekli'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
